import * as React from 'react';
import { Container, Grid, Typography, Box, Divider, Paper } from '@mui/material';
import ModernToolCard from '../components/ModernToolCard';
import { useTranslation } from "react-i18next";

const CATEGORIES = [
  { id: 'Preprocessing', labelEn: 'Preprocessing', labelTr: 'Önişleme' },
  { id: 'Morphology', labelEn: 'Morphology', labelTr: 'Morfoloji' },
  { id: 'Syntax', labelEn: 'Syntax', labelTr: 'Sözdizim' },
  { id: 'Analysis', labelEn: 'Analysis & Semantics', labelTr: 'Analiz ve Anlambilim' },
  { id: 'Generation', labelEn: 'Text Generation', labelTr: 'Metin Üretimi' },
  { id: 'General', labelEn: 'Other Tools', labelTr: 'Diğer Araçlar' }
];

const getCategory = (toolEnum) => {
  const mapping = {
    'Preprocessing': ['tokenizer', 'sentence-splitter', 'deasciifier', 'deasciifier_v2', 'sent_splitter_v2', 'tokenizer_version2'],
    'Morphology': ['lemmatizer', 'morphological_analyzer', 'morphological_disambiguator', 'morphological_analyzer_disambiguator'],
    'Syntax': ['boun_pars'],
    'Analysis': ['ermi', 'ner', 'question_answering', 'relx', 'sentiment_embeddings', 'bounti_sentiment_analyzer', 'targeted-sentiment-analysis', 'trvectors', 'postagger', 'spellcorrection', 'vowelizer'],
    'Generation': ['bounsumm']
  };
  for (const [cat, enums] of Object.entries(mapping)) {
    if (enums.includes(toolEnum)) return cat;
  }
  return 'General';
};

export default function MainPage({ tools }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  React.useEffect(() => {
    document.title = t('homepage');
  }, [lang, t]);

  if (!tools || tools.length === 0) {
    return (
      <Container sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">{t('loading')}...</Typography>
      </Container>
    );
  }

  const groupedTools = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = tools.filter(tool => getCategory(tool.enum) === cat.id);
    return acc;
  }, {});

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 800, 
            color: '#003366',
            mb: 2,
            letterSpacing: '-0.02em'
          }}
        >
          {t('homepage')}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ maxWidth: '800px', mx: 'auto', fontWeight: 400 }}
        >
          {lang === 'en' 
            ? 'Explore our collection of high-performance Turkish Natural Language Processing tools.' 
            : 'Yüksek başarımlı Türkçe Doğal Dil İşleme araçlarımızı keşfedin.'}
        </Typography>
      </Box>

      {CATEGORIES.map(category => {
        const toolsInCategory = groupedTools[category.id];
        if (!toolsInCategory || toolsInCategory.length === 0) return null;

        return (
          <Box key={category.id} sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#003366', 
                  mr: 2,
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '40px',
                    height: '4px',
                    bgcolor: '#0066cc',
                    borderRadius: 2
                  }
                }}
              >
                {lang === 'tr' ? category.labelTr : category.labelEn}
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                ({toolsInCategory.length} {lang === 'tr' ? 'araç' : 'tools'})
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {toolsInCategory.map((tool, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={tool.enum}>
                  <ModernToolCard tool={tool} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Container>
  );
}
